import { FC } from 'react'
import classNames from 'classnames'
import dynamic from 'next/dynamic'
import { PaddedContainer } from '@/atoms/PaddedContainer'
import { CategoryTabs } from '@/molecules/CategoryTabs'
import { isInvestmentBanner } from '@/organisms/InvestmentBanner/InvestmentBanner'
import { CallToAction, isCallToAction } from '@/page/CallToAction'
import { CollectionDisplay } from '@/page/CollectionDisplay'
import { Header, isHeader } from '@/page/Header'
import { isSpacer, Spacer } from '@/page/Spacer'
import { isTextBlock, TextBlock } from '@/page/TextBlock'
import {
  isAudienceInterest,
  isCollectionDisplay,
  isCollectionModel,
  isFeaturedProject,
  isFranchiseComponent,
  isUltrawideBannerGroupModel,
  PageDataContext,
} from '@/services/RenderService'
import { useUserRegion } from '@/services/UserRegionService'
import {
  ContentExperimentVariantCollection,
  InvestmentBanner as InvestmentBannerContent,
  PageContentItem,
  Spacer as SpacerType,
} from '@/types/codegen-contentful'
import { useSafeRouter } from '@/utils/nextUtils/safe-router'
import { Maybe } from '@/utils/types'
import { RecommendedBannerCarousel } from '@/views/WatchLandingView/RecommendedBannerCarousel'

// Dynamic imports for below-the-fold components to reduce initial bundle size
const Collection = dynamic(() => import('@/molecules/Collection').then((mod) => ({ default: mod.Collection })))
const FeaturedProjectSection = dynamic(() =>
  import('@/organisms/FeaturedProjectSection').then((mod) => ({ default: mod.FeaturedProjectSection })),
)
const FranchiseComponentSection = dynamic(() =>
  import('@/organisms/FranchiseComponentSection').then((mod) => ({ default: mod.FranchiseComponentSection })),
)
const InvestmentBanner = dynamic(() =>
  import('@/organisms/InvestmentBanner').then((mod) => ({ default: mod.InvestmentBanner })),
)
const InitialAudienceInterest = dynamic(() =>
  import('@/page/InitialAudienceInterest').then((mod) => ({ default: mod.InitialAudienceInterest })),
)

interface Props {
  content: Maybe<PageContentItem | ContentExperimentVariantCollection>
  pageDataContext: PageDataContext
  containerPosition?: number
}

export const ContentItem: FC<Props> = ({ content, pageDataContext, containerPosition = 0 }) => {
  const { asPath } = useSafeRouter()
  const { region } = useUserRegion()

  if (!content) return null

  const contentItem = renderContentItem(content, pageDataContext, region, containerPosition)
  return wrapContentItem(contentItem, content, pageDataContext, asPath)
}

const renderContentItem = (
  content: Maybe<PageContentItem | ContentExperimentVariantCollection>,
  pageDataContext: PageDataContext,
  region: Maybe<string>,
  containerPosition: number,
) => {
  if (region && content && 'region' in content && content?.region && !content.region.includes(region)) {
    return null
  } else if (isTextBlock(content)) {
    return <TextBlock textBlock={content} />
  } else if (isHeader(content)) {
    return <Header header={content} testId={content.sys.id} />
  } else if (isCollectionModel(content)) {
    return <Collection collection={content} pageDataContext={pageDataContext} containerPosition={containerPosition} />
  } else if (isCollectionDisplay(content)) {
    const isBannerDisplay = content.displayAs === 'banner'
    return (
      <>
        <CollectionDisplay
          collectionDisplay={content}
          pageDataContext={pageDataContext}
          containerPosition={containerPosition}
        />
        {isBannerDisplay && pageDataContext.contentItemConfig?.discoverTabs && (
          <CategoryTabs activeSlug={pageDataContext.contentItemConfig?.discoverTabsActiveSlug} />
        )}
      </>
    )
  } else if (isCallToAction(content)) {
    return <CallToAction callToAction={content} />
  } else if (isSpacer(content)) {
    return <Spacer spacer={content as SpacerType} />
  } else if (isUltrawideBannerGroupModel(content)) {
    const groupId = content?.sys.id
    return groupId ? (
      <>
        <RecommendedBannerCarousel />
        {pageDataContext.contentItemConfig?.discoverTabs && (
          <CategoryTabs activeSlug={pageDataContext.contentItemConfig?.discoverTabsActiveSlug} />
        )}
      </>
    ) : null
  } else if (isFeaturedProject(content)) {
    return (
      <FeaturedProjectSection
        testId={content.sys.id}
        featuredProjectId={content.sys.id}
        preview={pageDataContext.preview}
        containerPosition={containerPosition}
      />
    )
  } else if (isFranchiseComponent(content)) {
    return <FranchiseComponentSection featuredProjectId={content.sys.id} pageDataContext={pageDataContext} />
  } else if (isInvestmentBanner(content)) {
    const typedContent = content as InvestmentBannerContent
    return <InvestmentBanner {...typedContent} />
  } else if (isAudienceInterest(content)) {
    return <InitialAudienceInterest />
  }
  return null
}

const wrapContentItem = (
  contentItem: JSX.Element | null,
  content: Maybe<PageContentItem | ContentExperimentVariantCollection>,
  pageDataContext: PageDataContext,
  asPath: string,
) => {
  if (!contentItem) return null

  const isBannerCollectionDisplay = isCollectionDisplay(content) && content.displayAs === 'banner'

  if (
    (isUltrawideBannerGroupModel(content) ||
      isTextBlock(content) ||
      isCallToAction(content) ||
      isBannerCollectionDisplay) &&
    pageDataContext.contentItemConfig?.padding
  ) {
    return (
      <PaddedContainer className="overflow-x-hidden" fluid>
        {contentItem}
      </PaddedContainer>
    )
  } else if (isHeader(content)) {
    return (
      <PaddedContainer
        className={classNames('-mb-4 overflow-x-hidden', {
          '-mb-8': asPath.includes('watch'),
        })}
        fluid
      >
        {contentItem}
      </PaddedContainer>
    )
  } else {
    return <>{contentItem}</>
  }
}
